import { createContext, ProviderProps, useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Keycloak from 'keycloak-js'
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { setSilentSignIn, setUserConfirmed } from '@/slices/user'
import { LoadingPage } from '@/components/common/LoadingPage'
import { IUser } from '@namuho/types'

const keycloak = new Keycloak({
    url: import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.namuho.de',
    realm: 'namuho',
    clientId: 'app-client'
})

export interface IUserAttributes {
    email: string
    email_verified?: string
    given_name: string
    family_name: string
}

export interface UserCreationObject {
    firstname: string
    lastname: string
    email: string
    gender: string
    country: string
    city: string
    birthdate: string
    password: string
}

export interface IAuthContext {
    user: IUser | null
    signIn: (email: string, password: string, redirect?: boolean) => void
    signUp: (user: UserCreationObject) => void
    confirmEmail: (email: string, code: string, password?: string) => void
    resendCode: (email: string) => void
    changePassword: (oldPassword: string, newPassword: string) => void
    newPasswordRequired: (newPassword: string) => void
    forgotPassword: (email: string) => void
    resetPassword: (email: string, code: string, newPassword: string) => void
    signOut: (withRedirect?: boolean) => void
    globalSignOut: () => void
    loading: boolean
    session: any | null
    userAttributes: IUserAttributes | null
    keycloakUser: any | null
}

export const AuthContext = createContext({
    user: null as IUser | null,
    signIn: (_email: string, _password: string, _redirect?: boolean): void => {
        throw new Error('AuthContext.signIn() not implemented')
    },
    signUp: (user: UserCreationObject): void => {
        throw new Error('AuthContext.signUp() not implemented')
    },
    confirmEmail: (_email: string, _code: string, _password?: string): void => {
        throw new Error('AuthContext.confirmEmail() not implemented')
    },
    resendCode: (_email: string): void => {
        throw new Error('AuthContext.resendCode() not implemented')
    },
    changePassword: (_oldPassword: string, _newPassword: string): void => {
        throw new Error('AuthContext.changePassword() not implemented')
    },
    newPasswordRequired: (_password: string): void => {
        throw new Error('AuthContext.newPasswordRequired() not implemented')
    },
    forgotPassword: (_email: string): void => {
        throw new Error('AuthContext.forgotPassword() not implemented')
    },
    resetPassword: (_email: string, _code: string, _newPassword: string): void => {
        throw new Error('AuthContext.resetPassword() not implemented')
    },
    signOut: (_withRedirect?: boolean): void => {
        throw new Error('AuthContext.signOut() not implemented')
    },
    globalSignOut: (): void => {
        throw new Error('AuthContext.globalSignOut() not implemented')
    },
    getAccessToken: async (): Promise<string | undefined> => {
        throw new Error('AuthContext.getAccessToken() not implemented')
    },
    loading: true,
    session: null as any | null,
    userAttributes: null as IUserAttributes | null,
    keycloakUser: null as any | null,
})

export const AuthProvider = ({ children }: Omit<ProviderProps<IAuthContext>, 'value'>) => {
    const [loading, setLoading] = useState(true)
    const [session, setSession] = useState<any | null>(null)
    const [user, setUser] = useState<IUser | null>(null)
    const [userAttributes, setUserAttributes] = useState<IUserAttributes | null>(null)
    const [keycloakUser, setKeycloakUser] = useState<any | null>(null)
    const dispatch = useDispatch()

    const navigate = useNavigate()

    const signOut = useCallback(
        (withRedirect = true) => {
            if (keycloakUser) {
                keycloak.logout()
                toast.success('Erfolgreich abgemeldet')
                localStorage.removeItem('kc_token')
                localStorage.removeItem('kc_refreshToken')
                localStorage.removeItem('kc_idToken')
                setUser(null)
                setSession(null)
                setUserAttributes(null)
                if (withRedirect) navigate('/auth/sign-in')
            } else {
                console.error('No user currently signed in')
            }
        },
        [keycloakUser, navigate]
    )

    const globalSignOut = useCallback(() => {
        if (keycloakUser) {
            keycloak.logout()
            toast.success('Erfolgreich abgemeldet global')
            localStorage.removeItem('kc_token')
            localStorage.removeItem('kc_refreshToken')
            localStorage.removeItem('kc_idToken')
            setUser(null)
            setSession(null)
            setUserAttributes(null)
            navigate('/auth/sign-in')
        } else {
            console.error('No user currently signed in')
        }
    }, [keycloakUser, navigate])

    const getUser = useCallback((token?: string) => {
        const accessToken = token || keycloak.token
        if (accessToken) {
            fetch(`${import.meta.env.VITE_API_URL}/api/users/me`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
            })
                .then(async res => {
                    if (!res.ok) throw new Error('Failed to fetch user')
                    return res.json()
                })
                .then(data => {
                    console.log('User data loaded:', data)
                    setUser(data)
                    setLoading(false)
                })
                .catch(err => {
                    console.error('Error fetching user:', err)
                    setLoading(false)
                })
            
            // Also load Keycloak user info for userAttributes
            keycloak.loadUserInfo().then(userInfo => {
                setUserAttributes(userInfo as IUserAttributes)
            }).catch(err => {
                console.error('Error loading Keycloak user info:', err)
            })
        } else {
            console.error('No token available for getUser')
            setLoading(false)
        }
    }, [])

    const signIn = useCallback(
        async (email: string, password: string, redirect: boolean = true) => {
            try {
                const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password }),
                })

                if (!response.ok) {
                    throw new Error('Login failed')
                }

                const data = await response.json()
                
                // Manually set tokens on keycloak object
                keycloak.token = data.access_token
                keycloak.refreshToken = data.refresh_token
                keycloak.idToken = data.id_token
                
                localStorage.setItem('kc_token', data.access_token)
                localStorage.setItem('kc_refreshToken', data.refresh_token)
                localStorage.setItem('kc_idToken', data.id_token)
                
                // Parse JWT to allow token refresh to work without reload
                try {
                    keycloak.tokenParsed = JSON.parse(atob(data.access_token.split('.')[1]))
                } catch (e) {}
                
                setSession(data.access_token)
                setKeycloakUser(keycloak)
                getUser(data.access_token)
                dispatch(setSilentSignIn('YES'))
                if (redirect) {
                    navigate('/')
                }
            } catch (err) {
                console.error('Login error:', err)
                toast.error('Login failed')
                dispatch(setSilentSignIn('WRONG_PASSWORD'))
            }
        },
        [dispatch, navigate, getUser]
    )

    const signUp = useCallback((user: UserCreationObject) => {
        const { firstname, lastname, email, gender, birthdate, password, city, country } = user

        fetch(`${import.meta.env.VITE_API_URL}/api/users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ firstname, lastname, email, gender, birthdate, city, country, password })
        }).then(async res => {
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}))
                throw new Error(errorData.message || `API Error: ${res.status}`)
            }
            return res.json()
        })
            .then(data => {
                toast.success('Benutzer erfolgreich erstellt')
                // No redirect here, we stay in the creator flow
            }).catch(err => {
                console.error('User creation error:', err)
                toast.error(`Registrierung fehlgeschlagen: ${err.message}`)
            })
    }, [signIn])

    const confirmEmail = useCallback(
        (email: string, code: string, password?: string) => {
            fetch(`${import.meta.env.VITE_API_URL}/api/users/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code, password })
            }).then(async res => {
                if (!res.ok) {
                    const errorData = await res.json().catch(() => ({}))
                    throw new Error(errorData.message || 'Ungültiger Code')
                }
                return res.json()
            }).then(async data => {
                console.log('Verification API response:', data)
                toast.success('Email erfolgreich bestätigt')
                dispatch(setUserConfirmed())
                
                if (data.token) {
                    console.log('Token received, performing silent login...')
                    // Manually set tokens on keycloak object
                    keycloak.token = data.token.access_token
                    keycloak.refreshToken = data.token.refresh_token
                    keycloak.idToken = data.token.id_token
                    
                    localStorage.setItem('kc_token', data.token.access_token)
                    localStorage.setItem('kc_refreshToken', data.token.refresh_token)
                    localStorage.setItem('kc_idToken', data.token.id_token)
                    
                    // Parse JWT to allow token refresh to work without reload
                    try {
                        keycloak.tokenParsed = JSON.parse(atob(data.token.access_token.split('.')[1]))
                    } catch (e) {}
                    
                    setSession(data.token.access_token)
                    setKeycloakUser(keycloak)
                    getUser(data.token.access_token)
                    dispatch(setSilentSignIn('YES'))
                } else {
                    console.warn('No token received from verification API. Redirecting to sign-in.')
                    navigate('/auth/sign-in')
                }
            }).catch(err => {
                console.error('Email confirmation error:', err)
                toast.error(`Bestätigung fehlgeschlagen: ${err.message}`)
            })
        },
        [dispatch, navigate, getUser]
    )

    const resendCode = useCallback((email: string) => {
        fetch(`${import.meta.env.VITE_API_URL}/api/users/resend-code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
        }).then(async res => {
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}))
                throw new Error(errorData.message || 'Fehler beim Senden des Codes')
            }
            toast.success('Neuer Bestätigungscode wurde gesendet')
        }).catch(err => {
            console.error('Resend code error:', err)
            toast.error(`Senden fehlgeschlagen: ${err.message}`)
        })
    }, [])

    const changePassword = useCallback((oldPassword: string, newPassword: string) => {
        if (keycloakUser) {
            keycloak.changePassword(oldPassword, newPassword).then(() => {
                toast.success('Password changed successfully')
            }).catch(err => {
                console.error('Change password error:', err)
                toast.error('Change password failed')
            })
        } else {
            toast.error('No user logged in')
        }
    }, [keycloakUser])

    const newPasswordRequired = useCallback(
        (newPassword: string) => {
            if (keycloakUser) {
                keycloak.changePassword('', newPassword).then(() => {
                    toast.success('Password changed successfully')
                    navigate('/')
                }).catch(err => {
                    console.error('New password error:', err)
                    toast.error('New password failed')
                })
            } else {
                toast.error('No user logged in')
            }
        },
        [keycloakUser, navigate]
    )

    const forgotPassword = useCallback((email: string) => {
        keycloak.resetPassword({ username: email }).then(() => {
            toast.success('Password reset email sent')
            navigate(`/auth/reset-password/${email}`)
        }).catch(err => {
            console.error('Forgot password error:', err)
            toast.error('Forgot password failed')
        })
    }, [navigate])

    const resetPassword = useCallback(
        (email: string, code: string, newPassword: string) => {
            keycloak.confirmResetPassword({ username: email, code, newPassword }).then(() => {
                toast.success('Password reset successfully')
                navigate('/auth/sign-in')
            }).catch(err => {
                console.error('Reset password error:', err)
                toast.error('Reset password failed')
            })
        },
        [navigate]
    )

    useEffect(() => {
        const token = localStorage.getItem('kc_token') || undefined
        const refreshToken = localStorage.getItem('kc_refreshToken') || undefined
        const idToken = localStorage.getItem('kc_idToken') || undefined

        keycloak.init({ 
            onLoad: 'check-sso',
            token,
            refreshToken,
            idToken
        }).then(authenticated => {
            if (authenticated) {
                if (keycloak.token) localStorage.setItem('kc_token', keycloak.token)
                if (keycloak.refreshToken) localStorage.setItem('kc_refreshToken', keycloak.refreshToken)
                if (keycloak.idToken) localStorage.setItem('kc_idToken', keycloak.idToken)
                setKeycloakUser(keycloak)
                setSession(keycloak.token)
                getUser()
            } else {
                localStorage.removeItem('kc_token')
                localStorage.removeItem('kc_refreshToken')
                localStorage.removeItem('kc_idToken')
                setLoading(false)
            }
        }).catch(err => {
            console.error('Keycloak init error:', err)
            setLoading(false)
        })
    }, [getUser])

    const getAccessToken = useCallback(async () => {
        if (!keycloakUser || !keycloak.token) {
            return undefined
        }

        try {
            await keycloak.updateToken(30)
            if (keycloak.token) localStorage.setItem('kc_token', keycloak.token)
            if (keycloak.refreshToken) localStorage.setItem('kc_refreshToken', keycloak.refreshToken)
            if (keycloak.idToken) localStorage.setItem('kc_idToken', keycloak.idToken)
        } catch (err) {
            console.warn('Token refresh warning:', err)
        }

        return `Bearer ${keycloak.token}`
    }, [keycloakUser])

    if (loading) {
        return <LoadingPage />
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                signIn,
                signUp,
                confirmEmail,
                resendCode,
                changePassword,
                newPasswordRequired,
                forgotPassword,
                resetPassword,
                getAccessToken,
                signOut,
                globalSignOut,
                loading,
                session,
                userAttributes,
                keycloakUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    return useContext(AuthContext)
}
